/**
 * Analytics Context Manager
 *
 * Manages analytics tracking context across browser windows, tabs, and redirects.
 * Uses IndexedDB to persist data through window.open() and 3rd-party redirects.
 *
 * Flow:
 * 1. Deal feed/detail: Save context when user interacts
 * 2. Checkout: Retrieve and use context
 * 3. Payment: Retrieve and use context
 * 4. Purchase complete: Clean up context
 */

import { openDB, type DBSchema, type IDBPDatabase } from "idb";
import { devLog } from "./dev-log";
import type { ItemAttrs, ContextAttrs } from "~/types/analytics";

const DB_NAME = "blidz_analytics";
const DB_VERSION = 1;
const STORE_NAME = "contexts";
const CONTEXT_TTL_MS = 3600000; // 1 hour

/**
 * Analytics context data stored for a deal interaction
 */
export interface AnalyticsContext {
  dealId: string;
  productId: string;
  reqId: string;

  // Context attributes
  contextAttrs: ContextAttrs;

  // Item attributes (price at time of interaction)
  itemAttrs: ItemAttrs;

  // Timestamp for TTL
  timestamp: number;
}

/**
 * IndexedDB Schema
 */
interface AnalyticsDB extends DBSchema {
  contexts: {
    key: string; // dealId
    value: AnalyticsContext;
    indexes: {
      timestamp: number;
    };
  };
}

/**
 * Database instance (singleton)
 */
let dbPromise: Promise<IDBPDatabase<AnalyticsDB>> | null = null;

/**
 * Initialize and get database instance
 */
function getDB(): Promise<IDBPDatabase<AnalyticsDB>> {
  if (!dbPromise) {
    dbPromise = openDB<AnalyticsDB>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        // Create object store with dealId as key
        const store = db.createObjectStore(STORE_NAME, { keyPath: "dealId" });

        // Create index on timestamp for efficient expiration queries
        store.createIndex("timestamp", "timestamp");

        devLog("📦 IndexedDB store created:", STORE_NAME);
      },
    });
  }
  return dbPromise;
}

/**
 * Save analytics context for a deal
 * Returns the saved context
 */
export async function saveAnalyticsContext(
  dealId: string,
  productId: string,
  reqId: string,
  contextAttrs: ContextAttrs,
  itemAttrs: ItemAttrs
): Promise<AnalyticsContext | null> {
  try {
    const db = await getDB();
    const context: AnalyticsContext = {
      dealId,
      productId,
      reqId,
      contextAttrs,
      itemAttrs,
      timestamp: Date.now(),
    };

    await db.put(STORE_NAME, context);
    devLog("📝 Analytics context saved:", dealId, context);
    return context;
  } catch (error) {
    console.error("Failed to save analytics context:", error);
    return null;
  }
}

/**
 * Get analytics context for a deal
 */
export async function getAnalyticsContext(dealId: string): Promise<AnalyticsContext | null> {
  try {
    const db = await getDB();
    const context = await db.get(STORE_NAME, dealId);

    if (!context) {
      devLog("📭 No analytics context found for deal:", dealId);
      return null;
    }

    // Check if context has expired
    const age = Date.now() - context.timestamp;
    if (age > CONTEXT_TTL_MS) {
      devLog("⏰ Analytics context expired for deal:", dealId);
      await removeAnalyticsContext(dealId);
      return null;
    }

    devLog("📖 Analytics context retrieved:", dealId, context);
    return context;
  } catch (error) {
    console.error("Failed to get analytics context:", error);
    return null;
  }
}

/**
 * Update analytics context for a deal (merge with existing)
 * Accepts individual fields from contextAttrs and itemAttrs
 * Only updates non-null/non-undefined values
 * Returns the updated context
 */
export async function updateAnalyticsContext(
  dealId: string,
  updates: Partial<ContextAttrs & ItemAttrs>
): Promise<AnalyticsContext | null> {
  try {
    const existing = await getAnalyticsContext(dealId);
    if (!existing) {
      devLog("⚠️ Cannot update non-existent context for deal:", dealId);
      return null;
    }

    // Helper to merge only non-null/undefined values
    const mergeNonNull = <T extends object>(target: T, source: Partial<T>): T => {
      const result = { ...target };
      for (const key in source) {
        const value = source[key];
        if (value !== null && value !== undefined) {
          result[key] = value;
        }
      }
      return result;
    };

    // Merge updates into respective attribute objects
    const updatedContextAttrs = mergeNonNull(existing.contextAttrs, updates as Partial<ContextAttrs>);
    const updatedItemAttrs = mergeNonNull(existing.itemAttrs, updates as Partial<ItemAttrs>);

    // Save and return the updated context
    return await saveAnalyticsContext(
      existing.dealId,
      existing.productId,
      existing.reqId,
      updatedContextAttrs,
      updatedItemAttrs
    );
  } catch (error) {
    console.error("Failed to update analytics context:", error);
    return null;
  }
}

/**
 * Remove analytics context for a deal
 */
export async function removeAnalyticsContext(dealId: string): Promise<void> {
  try {
    const db = await getDB();
    await db.delete(STORE_NAME, dealId);
    devLog("🗑️ Analytics context removed:", dealId);
  } catch (error) {
    console.error("Failed to remove analytics context:", error);
  }
}

/**
 * Clean up expired contexts (using indexed query for efficiency)
 */
export async function cleanupExpiredContexts(): Promise<void> {
  try {
    const db = await getDB();
    const cutoffTime = Date.now() - CONTEXT_TTL_MS;

    // Use index to efficiently find all expired contexts
    const tx = db.transaction(STORE_NAME, "readwrite");
    const index = tx.store.index("timestamp");

    // Get all items with timestamp < cutoffTime
    const range = IDBKeyRange.upperBound(cutoffTime);
    const expiredContexts = await index.getAll(range);

    // Delete all expired contexts
    const deletePromises = expiredContexts.map(context => tx.store.delete(context.dealId));

    await Promise.all(deletePromises);
    await tx.done;

    if (expiredContexts.length > 0) {
      devLog(`🧹 Cleaned up ${expiredContexts.length} expired analytics contexts`);
      expiredContexts.forEach(context => {
        devLog("🧹 Cleaned up expired context:", context.dealId);
      });
    }
  } catch (error) {
    console.error("Failed to cleanup expired contexts:", error);
  }
}

/**
 * Get all analytics contexts (for debugging)
 */
export async function getAllContexts(): Promise<AnalyticsContext[]> {
  try {
    const db = await getDB();
    return await db.getAll(STORE_NAME);
  } catch (error) {
    console.error("Failed to get all contexts:", error);
    return [];
  }
}

/**
 * Clear all analytics contexts (for debugging/testing)
 */
export async function clearAllContexts(): Promise<void> {
  try {
    const db = await getDB();
    await db.clear(STORE_NAME);
    devLog("🗑️ All analytics contexts cleared");
  } catch (error) {
    console.error("Failed to clear all contexts:", error);
  }
}

// Auto-cleanup on module load
if (typeof window !== "undefined") {
  // Run cleanup on page load (async, doesn't block)
  cleanupExpiredContexts().catch(err => console.error("Initial cleanup failed:", err));

  // Also run periodic cleanup every 5 minutes
  setInterval(
    () => {
      cleanupExpiredContexts().catch(err => console.error("Periodic cleanup failed:", err));
    },
    5 * 60 * 1000
  );
}
